import '@kovix/ui/styles.css';
import { ApiError } from '@kovix/api-client';
import { ToastProvider } from '@kovix/ui';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { router } from '@/app/router/router';
import { clearTenantReadOnly, noteTenantReadOnly } from '@/app/shell/TenantStatusBanner';
import { BusyOverlay } from '@/shared/ui/BusyOverlay';

const queryClient = new QueryClient({
  // 13/10/2026 (QA staging: :submit / presign-upload 504 ở gateway nhưng máy chủ ĐÃ ghi → màn còn trạng thái cũ, bấm lại 412 version_conflict):
  // gateway cắt kết nối (502/504) không có nghĩa là thao tác thất bại → tải lại MỌI dữ liệu đang hiển thị để màn phản ánh trạng thái thật (ETag/version mới)
  // trước khi người dùng bấm lại. Thông điệp lỗi do từng module/HTTP_STATUS_VI đã có.
  mutationCache: new MutationCache({
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 504 || err.status === 502)) void queryClient.invalidateQueries();
      // KOVIX-380/63: gateway báo tenant chỉ đọc (SUSPENDED/TERMINATING) → banner toàn trang dù trạng thái /tenant/me đã cũ hoặc người dùng thiếu quyền đọc nó
      if (err instanceof ApiError && err.status === 403) noteTenantReadOnly(err.code);
    },
    // rà soát 13/10: một thao tác GHI thành công = tenant không còn chỉ đọc → gỡ banner dự phòng (tenant đã thanh toán / mở lại, hoặc đăng nhập tenant khác)
    onSuccess: () => clearTenantReadOnly(),
  }),
  defaultOptions: {
    queries: {
      // Lỗi nghiệp vụ (4xx) không retry; lỗi mạng/5xx retry có kiểm soát (TDD §10.2 phân loại lỗi)
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
        <BusyOverlay />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
